/**
 * ContactPicker — one recipient-picker to rule them all.
 *
 * Consolidates what were three independent, drifting implementations:
 *   1. NewTextComposerModal (SMS recipient picker)
 *   2. FaxDocumentButton's inline `showContactPicker` dropdown
 *   3. Ad-hoc "pick a phone" flows elsewhere
 *
 * All three loaded `/api/contacts`, applied their own filter, and had
 * subtly different empty states, keyboard handling, and phone
 * validation. Every new refactor of the contacts collection meant
 * touching three places and hoping the drift didn't bite. This
 * component fixes that by taking:
 *   * `channel`          — "phone" | "fax" | "email" (controls which
 *                          endpoint we look for on each contact row
 *                          and how the direct-entry field validates)
 *   * `filter`           — optional predicate applied on top of the
 *                          channel-endpoint filter (e.g. only vendors)
 *   * `allowDirectEntry` — show the "or type a number" strip
 *   * `onPick(contact, endpointValue)` — the caller receives the full
 *                          spine row PLUS the resolved endpoint value
 *                          so it can dispatch without a second lookup
 *   * `onPickRaw(digits)` — optional — direct-entry callback for
 *                          numbers not tied to a contact yet
 *
 * Read-only. Never writes to `/api/contacts`. A caller that needs
 * "add a new contact" should chain in QuickAddContactModal on close.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import {
  X as XIcon, Search, Loader2, Phone, Printer as FaxIcon,
  Mail, UserSearch,
} from "lucide-react";
import { toast } from "sonner";
import { contactsAPI } from "../../services/api";

const NAVY = "#032866";
const NAVY_HAIRLINE = "#E2E8F0";
const GOLD = "#F5B932";

const DIGITS = (s) => (s || "").replace(/\D/g, "");
const LAST10 = (s) => {
  const d = DIGITS(s);
  return d.length >= 10 ? d.slice(-10) : "";
};
const FMT = (s) => {
  const d = LAST10(s);
  if (d.length !== 10) return s || "";
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
};

const CHANNEL_META = {
  phone: {
    field: "phone_number",
    icon: Phone,
    entryLabel: "Or text a number directly",
    entryPlaceholder: "(414) 555-0199",
    entryValidateError: "Please enter a 10-digit US phone number.",
    validate: (raw) => LAST10(raw).length === 10,
    normalize: (raw) => LAST10(raw),
    getEndpoint: (c) => c.phone_number,
  },
  fax: {
    field: "fax_number",
    icon: FaxIcon,
    entryLabel: "Or fax a number directly",
    entryPlaceholder: "(262) 555-1234",
    entryValidateError: "Please enter a 10-digit fax number.",
    validate: (raw) => LAST10(raw).length === 10,
    normalize: (raw) => LAST10(raw),
    getEndpoint: (c) => c.fax_number,
  },
  email: {
    field: "email",
    icon: Mail,
    entryLabel: "Or email an address directly",
    entryPlaceholder: "jane@example.com",
    entryValidateError: "Please enter a valid email address.",
    validate: (raw) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((raw || "").trim()),
    normalize: (raw) => (raw || "").trim().toLowerCase(),
    getEndpoint: (c) => c.email,
  },
};

export default function ContactPicker({
  open,
  onClose,
  onPick,
  onPickRaw,
  title = "Pick a recipient",
  channel = "phone",
  filter,
  allowDirectEntry = true,
  testIdPrefix = "contact-picker",
}) {
  const meta = CHANNEL_META[channel] || CHANNEL_META.phone;
  const [q, setQ] = useState("");
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [raw, setRaw] = useState("");
  const searchRef = useRef(null);

  // Reset + autofocus each time we open.
  useEffect(() => {
    if (open) {
      setQ("");
      setRaw("");
      setTimeout(() => searchRef.current?.focus(), 50);
    }
  }, [open]);

  // Load contacts on open.
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoading(true);
    contactsAPI.getAll()
      .then((r) => { if (!cancelled) setContacts(r.data || []); })
      .catch(() => { if (!cancelled) setContacts([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open]);

  // Escape closes.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const filtered = useMemo(() => {
    // Channel gate: contacts must have an endpoint of the requested kind.
    let base = contacts.filter((c) => {
      const ep = meta.getEndpoint(c);
      if (!ep) return false;
      if (channel === "email") return meta.validate(ep);
      return LAST10(ep).length >= 10;
    });
    // Optional additional filter (e.g. only vendors).
    if (typeof filter === "function") base = base.filter(filter);

    if (!q) return base.slice(0, 40);
    const s = q.trim().toLowerCase();
    const dq = DIGITS(q);
    return base.filter((c) => {
      const name = `${c.first_name || ""} ${c.last_name || ""}`.trim().toLowerCase();
      const co = (c.company_name || c.company || "").toLowerCase();
      const epDigits = DIGITS(meta.getEndpoint(c));
      const epLower  = String(meta.getEndpoint(c) || "").toLowerCase();
      return (
        name.includes(s) ||
        co.includes(s) ||
        epLower.includes(s) ||
        (dq && epDigits.includes(dq))
      );
    }).slice(0, 40);
  }, [q, contacts, filter, meta, channel]);

  const pickContact = (c) => {
    const ep = meta.getEndpoint(c);
    if (!ep) return toast.error(`This contact has no ${channel} endpoint.`);
    const normalized = meta.normalize(ep);
    onPick?.(c, normalized);
    onClose?.();
  };

  const pickRaw = () => {
    if (!meta.validate(raw)) { toast.error(meta.entryValidateError); return; }
    const normalized = meta.normalize(raw);
    (onPickRaw || onPick)?.(null, normalized);
    onClose?.();
  };

  if (!open) return null;
  const EndpointIcon = meta.icon;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-start justify-center bg-black/50 pt-20 px-4"
      onClick={onClose}
      data-testid={testIdPrefix}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden"
      >
        <div
          className="flex items-center justify-between px-4 py-3"
          style={{ background: NAVY }}
        >
          <div className="flex items-center gap-2 text-white font-bold text-[14px]">
            <UserSearch className="w-4 h-4" />
            {title}
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-white/10"
            aria-label="Close"
            data-testid={`${testIdPrefix}-close`}
          >
            <XIcon className="w-4 h-4 text-white" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div>
            <label
              className="text-[10px] font-black uppercase tracking-wider block mb-1"
              style={{ color: NAVY }}
            >
              Find a saved contact
            </label>
            <div className="relative">
              <Search
                className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none"
                style={{ color: "#64748B" }}
              />
              <input
                ref={searchRef}
                type="search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={`Search name, company, ${channel === "email" ? "email" : "number"}…`}
                className="w-full h-9 pl-8 pr-3 rounded-md border text-[13px] font-medium focus:outline-none focus:ring-2"
                style={{ borderColor: NAVY_HAIRLINE, color: NAVY }}
                data-testid={`${testIdPrefix}-search`}
              />
            </div>
            <div
              className="mt-2 max-h-60 overflow-y-auto rounded border"
              style={{ borderColor: NAVY_HAIRLINE }}
            >
              {loading && (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="w-4 h-4 animate-spin" style={{ color: NAVY }} />
                </div>
              )}
              {!loading && filtered.length === 0 && (
                <div className="text-center text-[12px] text-slate-400 py-6" data-testid={`${testIdPrefix}-empty`}>
                  {q
                    ? `No contacts match. ${allowDirectEntry ? "Try the direct entry below." : ""}`
                    : `No contacts with a ${channel} endpoint on file yet.`}
                </div>
              )}
              {!loading && filtered.map((c) => {
                const displayName = `${c.first_name || ""} ${c.last_name || ""}`.trim();
                const secondary  = c.company_name || c.company || "";
                const epValue    = meta.getEndpoint(c);
                const epDisplay  = channel === "email" ? epValue : FMT(epValue);
                const primary = displayName || secondary || epDisplay || "Unnamed contact";
                const showSecondary = displayName ? secondary : "";
                return (
                  <button
                    key={c.id}
                    onClick={() => pickContact(c)}
                    className="w-full flex items-center gap-3 px-3 py-2 hover:bg-slate-50 border-b last:border-b-0 text-left"
                    style={{ borderColor: NAVY_HAIRLINE }}
                    data-testid={`${testIdPrefix}-contact-${c.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-semibold truncate" style={{ color: NAVY }}>
                        {primary}
                      </div>
                      <div className="text-[11px] text-slate-500 truncate">
                        {showSecondary ? `${showSecondary} · ` : ""}{epDisplay}
                      </div>
                    </div>
                    <EndpointIcon className="w-3.5 h-3.5 shrink-0" style={{ color: "#64748B" }} />
                  </button>
                );
              })}
            </div>
          </div>

          {allowDirectEntry && (
            <div>
              <label
                className="text-[10px] font-black uppercase tracking-wider block mb-1"
                style={{ color: NAVY }}
              >
                {meta.entryLabel}
              </label>
              <div className="flex gap-2">
                <input
                  type={channel === "email" ? "email" : "tel"}
                  inputMode={channel === "email" ? "email" : "numeric"}
                  value={raw}
                  onChange={(e) => setRaw(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && pickRaw()}
                  placeholder={meta.entryPlaceholder}
                  className="flex-1 h-10 px-3 rounded-md border text-[14px] font-medium focus:outline-none focus:ring-2"
                  style={{ borderColor: NAVY_HAIRLINE, color: NAVY }}
                  data-testid={`${testIdPrefix}-raw-input`}
                />
                <button
                  onClick={pickRaw}
                  disabled={!meta.validate(raw)}
                  className="h-10 px-4 rounded-md text-[13px] font-bold flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{ background: GOLD, color: NAVY }}
                  data-testid={`${testIdPrefix}-raw-go`}
                >
                  Pick
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
